import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { useCinematic } from "@/hooks/useCinematic";
import { useLanguage } from "@/context/LanguageContext";
import SeoHead, { orgSchema } from "@/components/SeoHead";
import { staticPath } from "@/lib/siteMap";
import Cinematic from "@/components/Cinematic";

// Secciones bajo el primer viewport: chunk aparte y montaje progresivo
// al acercarse al viewport (IntersectionObserver). El hero no las espera.
const Chapters = lazy(() => import("@/components/Chapters"));
const Services = lazy(() => import("@/components/Services"));
const Marquee = lazy(() => import("@/components/Marquee"));
const Marketing = lazy(() => import("@/components/Marketing"));
const Cases = lazy(() => import("@/components/Cases"));
const Contact = lazy(() => import("@/components/Contact"));

// Monta el contenido ~900px antes de entrar al viewport (sin CLS ni flashes)
function Deferred({ children }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || show) return undefined;
    if (!("IntersectionObserver" in window)) {
      setShow(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "900px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  return <div ref={ref}>{show ? children : null}</div>;
}

const HOME_SEO = {
  es: {
    title: "DEVOLUZION — Evolución Digital | Diseño Web, Software, IA & Growth",
    desc: "No hacemos páginas web. Construimos evolución digital: diseño UX/UI, desarrollo de software, ecommerce, automatización, agentes de IA, marketing digital, SEO y Google Ads para empresas en Chile.",
  },
  en: {
    title: "DEVOLUZION — Digital Evolution | Web Design, Software, AI & Growth",
    desc: "We don't make websites. We build digital evolution: UX/UI design, software development, ecommerce, automation, AI agents, digital marketing, SEO and Google Ads.",
  },
};

export default function Home() {
  const cinematic = useCinematic();
  const { lang } = useLanguage();
  const seo = HOME_SEO[lang];

  return (
    <>
      <SeoHead
        lang={lang}
        title={seo.title}
        desc={seo.desc}
        pathEs="/es/"
        pathEn="/en/"
        schemas={[
          orgSchema(lang),
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "DEVOLUZION",
            url: "https://devoluzion.com",
            inLanguage: lang === "es" ? "es-CL" : "en",
          },
        ]}
      />
      <main>
        {cinematic ? <Cinematic /> : (
          <Suspense fallback={null}>
            <Chapters />
          </Suspense>
        )}
        <Suspense fallback={null}>
          <Deferred><Services /></Deferred>
          <Deferred><Marquee /></Deferred>
          <Deferred><Marketing /></Deferred>
          <Deferred><Cases /></Deferred>
          <Deferred><Contact /></Deferred>
        </Suspense>
      </main>
    </>
  );
}

// keep staticPath referenced for potential internal links
void staticPath;
